import type { TerminalEvent } from "@cmmd-forge/contracts";
import { isLoopbackHostname, isPrivateIpv4Hostname } from "@cmmd-forge/shared/loopback";

const LOOPBACK_HOST_PATTERN =
  /^(localhost|[a-z0-9.-]+\.localhost|127(?:\.\d{1,3}){3}|\[[0:]+1\]|::1)(?::\d+)?(?:[/?#]|$)/i;
const TERMINAL_URL_PATTERN = /https?:\/\/[^\s"'`<>]+/gu;
const ANSI_ESCAPE_PATTERN = new RegExp(
  String.raw`\u001B(?:\][\s\S]*?(?:\u0007|\u001B\\)|[@-_][0-?]*[ -/]*[@-~])`,
  "gu",
);
const WRAPPED_LOCAL_PREVIEW_PORT_PATTERN =
  /(https?:\/\/(?:localhost|[a-z0-9.-]+\.localhost|127(?:\.\d{1,3}){3}|\[[0:]+1\]|::1|10(?:\.\d{1,3}){3}|172\.(?:1[6-9]|2\d|3[01])(?:\.\d{1,3}){2}|192\.168(?:\.\d{1,3}){2}):)\s+(\d{1,5})(?=[/?#\s]|$)/giu;

export function normalizePreviewUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  const hostCandidate = trimmed.split(/[/:?#]/, 1)[0] ?? "";
  const protocol =
    LOOPBACK_HOST_PATTERN.test(trimmed) || isPrivateIpv4Host(hostCandidate) ? "http" : "https";
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `${protocol}://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export function isAllowedPreviewUrl(input: string): boolean {
  try {
    const parsed = new URL(input);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function isLoopbackPreviewUrl(input: string): boolean {
  try {
    const parsed = new URL(input);
    return isLoopbackHostname(parsed.hostname);
  } catch {
    return false;
  }
}

export function isPrivateIpv4Host(hostname: string): boolean {
  return isPrivateIpv4Hostname(hostname);
}

export function localDevServerPreviewPort(input: string): number | null {
  try {
    const parsed = new URL(input);
    if (
      parsed.protocol !== "http:" ||
      (!isLoopbackPreviewUrl(parsed.toString()) && !isPrivateIpv4Host(parsed.hostname)) ||
      !parsed.port
    ) {
      return null;
    }
    const port = Number(parsed.port);
    return Number.isInteger(port) && port > 0 && port <= 65_535 ? port : null;
  } catch {
    return null;
  }
}

function trimTerminalUrlCandidate(input: string): string {
  let output = input.replace(/[.,;!?]+$/u, "");
  while (output.endsWith(")") || output.endsWith("]") || output.endsWith("}")) {
    const close = output.at(-1);
    if (!close) break;
    const open = close === ")" ? "(" : close === "]" ? "[" : "{";
    const opens = output.split(open).length - 1;
    const closes = output.split(close).length - 1;
    if (opens >= closes) break;
    output = output.slice(0, -1);
  }
  return output;
}

function normalizeTerminalPreviewText(input: string): string {
  return input
    .replace(ANSI_ESCAPE_PATTERN, "")
    .replaceAll(/./gsu, (character) => (isTerminalControlCharacter(character) ? "" : character))
    .replace(WRAPPED_LOCAL_PREVIEW_PORT_PATTERN, "$1$2");
}

function isTerminalControlCharacter(character: string): boolean {
  const code = character.charCodeAt(0);
  return (
    (code >= 0 && code <= 8) ||
    code === 11 ||
    code === 12 ||
    (code >= 14 && code <= 31) ||
    code === 127
  );
}

export function extractLocalDevServerPreviewUrl(input: string): string | null {
  let fallbackUrl: string | null = null;
  let loopbackUrl: string | null = null;

  const normalizedInput = normalizeTerminalPreviewText(input);
  TERMINAL_URL_PATTERN.lastIndex = 0;
  for (const match of normalizedInput.matchAll(TERMINAL_URL_PATTERN)) {
    const normalized = normalizePreviewUrl(trimTerminalUrlCandidate(match[0]));
    if (!normalized || !localDevServerPreviewPort(normalized)) {
      continue;
    }
    fallbackUrl = normalized;
    if (isLoopbackPreviewUrl(normalized)) {
      loopbackUrl = normalized;
    }
  }

  return loopbackUrl ?? fallbackUrl;
}

export function latestTerminalDevServerPreviewUrl(
  entries: ReadonlyArray<{ readonly event: TerminalEvent }>,
): string | null {
  let latestUrl: string | null = null;
  for (const entry of entries) {
    const event = entry.event;
    const text =
      event.type === "output"
        ? event.data
        : event.type === "started" || event.type === "restarted"
          ? event.snapshot.history
          : "";
    const url = extractLocalDevServerPreviewUrl(text);
    if (url) {
      latestUrl = url;
    }
  }
  return latestUrl;
}
